import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useState } from 'react';

type TheOtherClimateCrisisChapter3Props = {
  dataBarChart: TheOtherClimateCrisisData['correct_knowledge_of_cc_younger'];
};

export function TheOtherClimateCrisis2023Chapter3(props: TheOtherClimateCrisisChapter3Props) {
  const { dataBarChart } = props;

  const formatValue = useNumberFormatting('percent');

  const [chartVisible, setChartVisible] = useState(false);

  const handleStepEnter = () => {
    setChartVisible(true);
  };

  const getLabel = (key: string) => {
    return t({
      id: `the-other-climate-crisis-2023.chapter-3.chart.bar-labels`,
      message: select(key, {
        lic: 'Low-income countries',
        lmc: 'Lower-middle income countries',
        umc: 'Upper-middle income countries',
        hic: 'High-income countries',
      }),
    });
  };

  const chartData = Object.entries(dataBarChart).map(([key, value]) => ({
    label: getLabel(key),
    value: value as number,
  }));

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <HorizontalBarChart
          label={t({
            id: 'the-other-climate-crisis-2023.chapter-3.chart-label',
            message: '% of young people who understand climate change',
          })}
          data={chartData}
          formatValue={formatValue}
          visible={chartVisible}
        />
      </ScrollGraphic>

      <ScrollSteps offset={0.7} onStepEnter={handleStepEnter}>
        <ScrollStep>
          <StoryText>
            <Trans id={`the-other-climate-crisis-2023.chapter-3.text-1`}>
              It's clear that climate change understanding among young people globally is{' '}
              <AnnotatedType>far from complete.</AnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id={`the-other-climate-crisis-2023.chapter-3.text-2`}>
              This is especially true in{' '}
              <PrimaryAnnotatedType>poorer countries</PrimaryAnnotatedType>—whose people have
              contributed least to the crisis— yet are most exposed to it.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id={`the-other-climate-crisis-2023.chapter-3.text-3`}>
              On average, {formatValue(dataBarChart.hic)} of young people in{' '}
              <PrimaryAnnotatedType>high-income countries</PrimaryAnnotatedType> say they have heard
              of climate change and are able to define it correctly.
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id={`the-other-climate-crisis-2023.chapter-3.text-4`}>
              The share is just {formatValue(dataBarChart.lic)} on average in{' '}
              <PrimaryAnnotatedType>low-income countries.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
